// src/pages/SignalsOverview.tsx
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaBell,
  FaCalendarCheck,
  FaExclamationTriangle,
  FaHourglassHalf,
} from "react-icons/fa";

import { useCan } from "../authz/useCan";
import {
  LdsOverviewCard,
  LdsOverviewEmpty,
  LdsOverviewGrid,
  LdsOverviewHero,
  LdsOverviewPage,
  LdsOverviewSection,
} from "../components/lds/LdsOverview";

export default function SignalsOverview() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const canSeeUnassigned = useCan("signals.unassigned-children", "view");
  const canSeeWaitingConflicts = useCan("signals.waiting-list-conflicts", "view");
  const canSeeCurrentSchoolYearCheck = useCan("signals.current-school-year", "view");

  const visibleSections = [
    canSeeUnassigned,
    canSeeWaitingConflicts,
    canSeeCurrentSchoolYearCheck,
  ].filter(Boolean).length;

  return (
    <LdsOverviewPage>
      <LdsOverviewHero
        icon={FaBell}
        eyebrow={t(
          "signals.overview.eyebrow",
          { defaultValue: "Signals" }
        )}
        title={t("signals.overview.title", {
          defaultValue: "Signals",
        })}
        description={t(
          "signals.overview.description",
          {
            defaultValue:
              "Review situations that require administrative attention.",
          }
        )}
      />

      {visibleSections > 0 ? (
        <LdsOverviewSection
          title={t(
            "signals.overview.sections.title",
            { defaultValue: "Attention required" }
          )}
          description={t(
            "signals.overview.sections.description",
            {
              defaultValue:
                "Choose a signal category to review.",
            }
          )}
        >
          <LdsOverviewGrid>
            {canSeeUnassigned ? (
              <LdsOverviewCard
                icon={FaExclamationTriangle}
                eyebrow={t(
                  "signals.overview.cards.unassigned.eyebrow",
                  { defaultValue: "Placement" }
                )}
                title={t(
                  "signals.overview.cards.unassigned.title",
                  {
                    defaultValue:
                      "Unassigned children",
                  }
                )}
                description={t(
                  "signals.overview.cards.unassigned.description",
                  {
                    defaultValue:
                      "Children without an active classroom assignment.",
                  }
                )}
                actionLabel={t("common.open", {
                  defaultValue: "Open",
                })}
                onClick={() =>
                  navigate(
                    "/reports/unassigned-children"
                  )
                }
                animationIndex={0}
              />
            ) : null}

            {canSeeWaitingConflicts ? (
              <LdsOverviewCard
                icon={FaHourglassHalf}
                eyebrow={t(
                  "signals.overview.cards.waitingConflicts.eyebrow",
                  { defaultValue: "Interest list" }
                )}
                title={t(
                  "signals.overview.cards.waitingConflicts.title",
                  {
                    defaultValue:
                      "Interest list conflicts",
                  }
                )}
                description={t(
                  "signals.overview.cards.waitingConflicts.description",
                  {
                    defaultValue:
                      "Children on the interest list that already have a classroom relation.",
                  }
                )}
                actionLabel={t("common.open", {
                  defaultValue: "Open",
                })}
                onClick={() =>
                  navigate(
                    "/reports/waiting-with-class"
                  )
                }
                animationIndex={1}
              />
            ) : null}

            {canSeeCurrentSchoolYearCheck ? (
              <LdsOverviewCard
                icon={FaCalendarCheck}
                eyebrow={t("signals.overview.cards.currentSchoolYear.eyebrow", {
                  defaultValue: "Classrooms",
                })}
                title={t("signals.overview.cards.currentSchoolYear.title", {
                  defaultValue: "Current school year check",
                })}
                description={t("signals.overview.cards.currentSchoolYear.description", {
                  defaultValue:
                    "Find children whose classroom assignment is not marked as their current school year.",
                })}
                actionLabel={t("common.open", { defaultValue: "Open" })}
                onClick={() => navigate("/reports/current-school-year-conflicts")}
                animationIndex={2}
              />
            ) : null}
          </LdsOverviewGrid>
        </LdsOverviewSection>
      ) : (
        <LdsOverviewEmpty>
          {t("signals.overview.empty", {
            defaultValue:
              "No signal sections are available with your current permissions.",
          })}
        </LdsOverviewEmpty>
      )}
    </LdsOverviewPage>
  );
}
